import React, { useEffect, useState } from "react";
import { Star, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";

export function ReviewDialog({ open, onClose }) {
  const [rating, setRating] = useState(5);
  const [quote, setQuote] = useState("");
  const [role, setRole] = useState("");
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!consent) return toast.error("Merci de donner votre accord pour publier l'avis.");
    if (!quote.trim()) return toast.error("Écrivez quelques mots.");
    setSaving(true);
    try {
      await api.post("/reviews", { rating, quote, role, consent });
      toast.success("Merci ! Votre avis sera publié après une courte validation.");
      setQuote(""); setConsent(false); onClose();
    } catch (e) {
      toast.error("Envoi impossible pour le moment.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="review-dialog">
        <DialogTitle className="font-display text-xl font-bold">Votre avis compte</DialogTitle>
        <DialogDescription>Partagez votre expérience. Avec votre accord, elle pourra être affichée sur notre site.</DialogDescription>
        <div className="mt-2 space-y-4">
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} onClick={() => setRating(s)} data-testid={`review-star-${s}`}>
                <Star className={`w-7 h-7 ${s <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
              </button>
            ))}
          </div>
          <Textarea value={quote} onChange={(e) => setQuote(e.target.value)} rows={3} placeholder="Ce que Le Café des Marchés vous apporte au quotidien…" data-testid="review-quote-input" />
          <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Votre métier et ville (ex : Agence événementielle, Lyon)" data-testid="review-role-input" />
          <div className="flex items-center justify-between rounded-xl border border-border p-3">
            <p className="text-sm text-slate-600 pr-3">J'autorise la publication de mon avis.</p>
            <Switch checked={consent} onCheckedChange={setConsent} data-testid="review-consent-toggle" />
          </div>
          <Button onClick={submit} disabled={saving} className="w-full rounded-full" data-testid="review-submit-btn">{saving ? "Envoi…" : "Envoyer mon avis"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AdminReviews() {
  const [pending, setPending] = useState([]);
  const load = () => api.get("/reviews/pending").then(({ data }) => setPending(data.reviews || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const approve = async (rid) => {
    await api.post(`/reviews/${rid}/approve`);
    toast.success("Avis publié");
    setPending((p) => p.filter((r) => r.review_id !== rid));
  };

  if (pending.length === 0) return null;
  return (
    <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="admin-reviews-panel">
      <div className="flex items-center gap-2 font-semibold text-amber-800 mb-3"><ShieldCheck className="w-4 h-4" /> Avis en attente de validation ({pending.length})</div>
      <div className="space-y-3">
        {pending.map((r) => (
          <div key={r.review_id} className="rounded-xl bg-white border border-amber-200 p-4 flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-1 text-amber-400 mb-1">{"★".repeat(r.rating)}</div>
              <p className="text-sm text-slate-700">« {r.quote} »</p>
              <p className="text-xs text-slate-500 mt-1">{r.name}{r.role ? ` — ${r.role}` : ""}</p>
            </div>
            <Button size="sm" onClick={() => approve(r.review_id)} className="rounded-full shrink-0" data-testid={`approve-review-${r.review_id}`}><Check className="w-4 h-4 mr-1" /> Publier</Button>
          </div>
        ))}
      </div>
    </div>
  );
}
